import { ModelBase } from "Core/Models";
import { isEmptyOrWhitespace } from "Core/Utils/Utils";
import { action, computed, observable } from "mobx";
import moment, { Moment } from "moment";

export class ReservationType1Model extends ModelBase<ReservationType1Model, ReservationType1DTO> {
    //#region Properties
    @observable
    public id: string | undefined = "";

    @observable
    public reservationTypeId: string = "";

    @observable
    public reservationType: string = "";

    @observable
    public propertyId: string = "";

    @observable
    public salesAgentIdentityUserId: string = "";

    @observable
    public salesAgentName: string = "";

    @observable
    public salesAdvisorContactNumber: string = "";

    @observable
    public salesAdvisorEmail: string = "";

    @observable
    public reservationFeePaid: string = "";

    @observable
    public agreedPurchasePrice: string = "";

    @observable
    public pdfUrl: string = "";

    @observable
    public pdfUrlSigned: string = "";

    @observable
    public pdfDocumentId: string = "";

    @observable
    public memorandumOfSalePDFDocumentId: string = "";

    @observable
    public expiryDate: Moment | null = null;

    @observable
    public anticipatedCompletedDateMonth: string = "";

    @observable
    public anticipatedCompletedDateYear: string = "";

    @observable
    public anticipatedCompletedDateFull: string = "";

    @observable
    public exchangeByDate: Moment | null = null;

    @observable
    public isFreehold: boolean = false;

    @observable
    public leaseStartDate: Moment | null = null;

    @observable
    public leaseYears: number = 0;

    @observable
    public initialGroundRent: string = "";

    @observable
    public managementServiceFees: string = "";

    @observable
    public pdfGenerated: boolean = false;

    @observable
    public pdfGeneratedDate: Moment | null = null;

    @observable
    public isComplete: boolean = false;

    @observable
    public completedDate: Moment | null = null;

    @observable
    public completedIdentityUserId: string = "";

    @observable
    public estateCharges: string = "";

    @observable
    public reservationDate: Moment | null = null;

    @observable
    public buildStage: string = "";

    @observable
    public isRoofedAndWaterproof?: boolean = undefined;
    //#endregion Properties

    @computed
    public get exchangeDateFormatted() {
        return this.exchangeByDate !== null && this.exchangeByDate !== undefined ? this.exchangeByDate.format("DD/MM/YYYY") : "";
    }

    @computed
    public get reservationDateFormatted() {
        return this.reservationDate !== null && this.reservationDate !== undefined ? this.reservationDate.format("DD/MM/YYYY") : "";
    }

    @computed
    public get leaseStartDateFormatted() {
        return this.leaseStartDate !== null && this.leaseStartDate !== undefined ? this.leaseStartDate.format("DD/MM/YYYY") : "";
    }

    @computed
    public get expiryDateFormatted() {
        return this.expiryDate !== null && this.expiryDate !== undefined ? this.expiryDate.format("DD/MM/YYYY") : "";
    }

    @computed
    public get completedDateFormatted() {
        return this.completedDate !== null && this.completedDate !== undefined ? this.completedDate.format("DD/MM/YYYY") : "";
    }

    @computed
    public get pdfGeneratedDateFormatted() {
        return this.pdfGeneratedDate !== null && this.pdfGeneratedDate !== undefined ? this.pdfGeneratedDate.format("DD/MM/YYYY") : "";
    }

    @action
    public fromDto = (reservationDto: ReservationType1DTO): void => {
        this.id = reservationDto.id;
        this.reservationTypeId = reservationDto.reservationTypeId;
        this.reservationType = reservationDto.reservationType;
        this.propertyId = reservationDto.propertyId;
        this.salesAgentIdentityUserId = reservationDto.salesAgentIdentityUserId;
        this.salesAgentName = reservationDto.salesAgentName;
        this.salesAdvisorContactNumber = reservationDto.salesAdvisorContactNumber;
        this.salesAdvisorEmail = reservationDto.salesAdvisorEmail;
        this.reservationFeePaid = reservationDto.reservationFeePaid;
        this.agreedPurchasePrice = reservationDto.agreedPurchasePrice;
        this.pdfUrl = reservationDto.pdfUrl;
        this.pdfUrlSigned = reservationDto.pdfUrlSigned;
        this.pdfDocumentId = reservationDto.pdfDocumentId;
        this.memorandumOfSalePDFDocumentId = reservationDto.memorandumOfSalePDFDocumentId;
        this.expiryDate = !isEmptyOrWhitespace(reservationDto.expiryDate) ? moment.tz(reservationDto.expiryDate, "Europe/London") : null;
        this.isFreehold = reservationDto.isFreehold;
        this.leaseStartDate = !isEmptyOrWhitespace(reservationDto.leaseStartDate) ? moment.tz(reservationDto.leaseStartDate, "Europe/London") : null;
        this.leaseYears = reservationDto.leaseYears;
        this.initialGroundRent = reservationDto.initialGroundRent;
        this.managementServiceFees = reservationDto.managementServiceFees;
        this.pdfGenerated = reservationDto.pdfGenerated;
        this.pdfGeneratedDate = !isEmptyOrWhitespace(reservationDto.pdfGeneratedDate) ? moment.tz(reservationDto.pdfGeneratedDate, "Europe/London") : null;
        this.isComplete = reservationDto.isComplete;
        this.completedDate = !isEmptyOrWhitespace(reservationDto.completedDate) ? moment.tz(reservationDto.completedDate, "Europe/London") : null;
        this.completedIdentityUserId = reservationDto.completedIdentityUserId;
        this.exchangeByDate = !isEmptyOrWhitespace(reservationDto.exchangeByDate) ? moment.tz(reservationDto.exchangeByDate, "Europe/London") : null;
        this.anticipatedCompletedDateMonth = reservationDto.anticipatedCompletedDateMonth;
        this.anticipatedCompletedDateYear = reservationDto.anticipatedCompletedDateYear;
        this.anticipatedCompletedDateFull = reservationDto.anticipatedCompletedDateFull;
        this.estateCharges = reservationDto.estateCharges;
        this.reservationDate = !isEmptyOrWhitespace(reservationDto.reservationDate) ? moment.tz(reservationDto.reservationDate, "Europe/London") : null;
        this.buildStage = reservationDto.buildStage;
        this.isRoofedAndWaterproof = reservationDto.isRoofedAndWaterproof;
    };

    public toDto = (): ReservationType1DTO => {
        return {
            id: this.id,
            reservationTypeId: this.reservationTypeId,
            reservationType: this.reservationType,
            propertyId: this.propertyId,
            salesAgentIdentityUserId: this.salesAgentIdentityUserId,
            salesAgentName: this.salesAgentName,
            salesAdvisorContactNumber: this.salesAdvisorContactNumber,
            salesAdvisorEmail: this.salesAdvisorEmail,
            reservationFeePaid: this.reservationFeePaid,
            agreedPurchasePrice: this.agreedPurchasePrice,
            pdfUrl: this.pdfUrl,
            pdfUrlSigned: this.pdfUrlSigned,
            pdfDocumentId: this.pdfDocumentId,
            memorandumOfSalePDFDocumentId: this.memorandumOfSalePDFDocumentId,
            expiryDate: this.expiryDate ? this.expiryDate.toString() : "",
            isFreehold: this.isFreehold,
            leaseStartDate: this.leaseStartDate ? this.leaseStartDate.toString() : "",
            leaseYears: this.leaseYears,
            initialGroundRent: this.initialGroundRent,
            managementServiceFees: this.managementServiceFees,
            pdfGenerated: this.pdfGenerated,
            pdfGeneratedDate: this.pdfGeneratedDate ? this.pdfGeneratedDate.toString() : "",
            isComplete: this.isComplete,
            completedDate: this.completedDate ? this.completedDate.toString() : "",
            completedIdentityUserId: this.completedIdentityUserId,
            exchangeByDate: this.exchangeByDate ? this.exchangeByDate.toString() : "",
            anticipatedCompletedDateMonth: this.anticipatedCompletedDateMonth,
            anticipatedCompletedDateYear: this.anticipatedCompletedDateYear,
            anticipatedCompletedDateFull: this.anticipatedCompletedDateFull,
            estateCharges: this.estateCharges,
            reservationDate: this.reservationDate ? this.reservationDate.toString() : "",
            buildStage: this.buildStage,
            isRoofedAndWaterproof: this.isRoofedAndWaterproof,
        };
    };
}

export interface ReservationType1DTO {
    id: string | undefined;
    reservationTypeId: string;
    reservationType: string;
    propertyId: string;
    salesAgentIdentityUserId: string;
    salesAgentName: string;
    salesAdvisorContactNumber: string;
    salesAdvisorEmail: string;
    reservationFeePaid: string;
    agreedPurchasePrice: string;
    pdfUrl: string;
    pdfUrlSigned: string;
    pdfDocumentId: string;
    memorandumOfSalePDFDocumentId: string;
    expiryDate: string;
    anticipatedCompletedDateMonth: string;
    anticipatedCompletedDateYear: string;
    anticipatedCompletedDateFull: string;
    exchangeByDate: string;
    isFreehold: boolean;
    leaseStartDate: string;
    leaseYears: number;
    initialGroundRent: string;
    managementServiceFees: string;
    pdfGenerated: boolean;
    pdfGeneratedDate: string;
    isComplete: boolean;
    completedDate: string;
    completedIdentityUserId: string;
    estateCharges: string;
    reservationDate: string;
    buildStage: string;
    ActualStatus?: number;
    isRoofedAndWaterproof?: boolean;
}
